import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, Loader2, Download, FileText, File } from "lucide-react";
import { toast } from "sonner";

export default function EvidencePreviewModal({ evidence, onClose, onUpdated }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!evidence) return;
    setLoading(true);
    setResult(null);
    api
      .get(`/evidence/${evidence.id}/download`, { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        setBlobUrl(url);
      })
      .finally(() => setLoading(false));
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidence?.id]);

  if (!evidence) return null;

  const ct = (evidence.content_type || "").toLowerCase();
  const isImg = ct.startsWith("image/");
  const isPdf = ct === "application/pdf";
  const isText = ct.startsWith("text/") || ct === "application/json";

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/evidence/${evidence.id}/analyze`);
      setResult(data);
      onUpdated?.();
      toast.success("Marianne analysed this evidence.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const downloadOriginal = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = evidence.original_filename;
    a.click();
  };

  return (
    <Dialog open={!!evidence} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="evidence-preview-modal">
        <DialogHeader className="px-6 py-4 border-b border-[#E8E3F0]">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="label-eyebrow mb-1">{evidence.content_type}</div>
              <DialogTitle className="font-serif text-2xl leading-tight truncate">
                {evidence.original_filename}
              </DialogTitle>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" className="rounded-none border-[#E8E3F0]" onClick={downloadOriginal} data-testid="preview-download-btn">
                <Download size={14} className="mr-1" /> Download
              </Button>
              <Button
                size="sm"
                className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]"
                onClick={analyze}
                disabled={analyzing}
                data-testid="preview-analyze-btn"
              >
                {analyzing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                <span className="ml-1.5">Analyse with AI</span>
              </Button>
            </div>
          </div>
        </DialogHeader>
        <div className="grid md:grid-cols-3 gap-0 overflow-hidden flex-1 min-h-0">
          <div className="md:col-span-2 border-r border-[#E8E3F0] bg-[#FAFAF8] overflow-auto">
            {loading ? (
              <div className="p-8 text-sm text-[#4A5568]">Loading preview…</div>
            ) : isImg ? (
              <img src={blobUrl} alt={evidence.original_filename} className="w-full h-auto object-contain" />
            ) : isPdf ? (
              <iframe src={blobUrl} title="preview" className="w-full h-[70vh] bg-white border-0" />
            ) : isText ? (
              <TextPreview url={blobUrl} />
            ) : (
              <div className="p-12 text-center">
                <File size={32} className="text-[#C5A059] mx-auto mb-3" strokeWidth={1.4} />
                <div className="font-serif text-xl">No inline preview</div>
                <div className="text-sm text-[#4A5568] mt-2">
                  This file type ({evidence.content_type || "unknown"}) cannot be previewed in-browser.
                </div>
              </div>
            )}
          </div>
          <div className="md:col-span-1 overflow-auto p-5 space-y-4">
            <div>
              <div className="label-eyebrow mb-2">Description</div>
              <div className="text-sm text-[#2A0F5A]/80">{evidence.description || <span className="italic text-[#4A5568]">None</span>}</div>
            </div>
            <div>
              <div className="label-eyebrow mb-2">Tags</div>
              <div className="flex flex-wrap gap-1">
                {(result?.tags ?? evidence.tags ?? []).length ? (
                  (result?.tags ?? evidence.tags).map((t) => (
                    <span key={t} className="text-[10px] tracking-[0.15em] uppercase font-semibold px-2 py-0.5 border border-[#C5A059] text-[#8a6b1f] bg-[#C5A059]/10">
                      {t}
                    </span>
                  ))
                ) : (
                  <span className="text-xs italic text-[#4A5568]">No tags yet</span>
                )}
              </div>
            </div>
            {result?.summary && (
              <div>
                <div className="label-eyebrow mb-2">AI summary</div>
                <div className="text-sm leading-relaxed text-[#2A0F5A]/80">{result.summary}</div>
              </div>
            )}
            {result?.extracted_text && (
              <div>
                <div className="label-eyebrow mb-2 flex items-center gap-1.5"><FileText size={12} /> Extracted text</div>
                <pre className="text-xs whitespace-pre-wrap font-mono bg-[#FAFAF8] border border-[#E8E3F0] p-3 max-h-72 overflow-auto">{result.extracted_text}</pre>
              </div>
            )}
            {!result && !analyzing && (
              <div className="text-xs text-[#4A5568] leading-relaxed border border-dashed border-[#E8E3F0] p-3">
                Click <strong>Analyse with AI</strong> to have Marianne read this file (OCR for images), suggest tags, and extract a summary.
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TextPreview({ url }) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!url) return;
    fetch(url).then((r) => r.text()).then(setText);
  }, [url]);
  return (
    <pre className="text-xs whitespace-pre-wrap font-mono p-6 bg-white h-full overflow-auto">{text}</pre>
  );
}
